import { createAsyncThunk } from "@reduxjs/toolkit"

import { handleApiError } from "../../../../Utils/thunkHelpers"
import * as services from "../services"
import { PUBLIC_API_SLICE_NAME } from "./publicApi.constants"

/* ------------- API keys ------------- */

// The legacy saga ran these three outside `apiGeneratorBase` so a (re)generate could chain
// the list refresh afterwards - kept here as a plain `dispatch(getApiKeys())` follow-up.
export const getApiKeys = createAsyncThunk(
  `${PUBLIC_API_SLICE_NAME}/get_api_keys`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getApiKeysList(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

export const generateApiKey = createAsyncThunk(
  `${PUBLIC_API_SLICE_NAME}/generate_api_key`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.generateApiKey(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    dispatch(getApiKeys())
    return response.data
  }
)

export const regenerateApiKey = createAsyncThunk(
  `${PUBLIC_API_SLICE_NAME}/regenerate_api_key`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.regenerateApiKey(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    dispatch(getApiKeys())
    return response.data
  }
)

/* ------------- Webhook endpoints ------------- */

export const getEndpoints = createAsyncThunk(
  `${PUBLIC_API_SLICE_NAME}/get_endpoints`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getEndpoints(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

export const getEndpoint = createAsyncThunk(
  `${PUBLIC_API_SLICE_NAME}/get_endpoint`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getEndpoint(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

// The endpoint details page used to pass `onSuccess` in the payload for this one; it now
// drives that off `.unwrap()`.
export const changeEndpointStatus = createAsyncThunk(
  `${PUBLIC_API_SLICE_NAME}/change_endpoint_status`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.changeEndpointStatus(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

// Mirrors the old `apiGeneratorDelete` saga branch rather than the generic
// `apiGeneratorBase` one: a 204 with no body still counts as success, and the success
// payload is `{ id, message }` instead of the raw response.
export const deleteEndpoint = createAsyncThunk(
  `${PUBLIC_API_SLICE_NAME}/delete_endpoint`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.deleteEndpoint(params)
    if (!response.ok && response.status !== 204) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return {
      id: params,
      message: response.data?.message?.detail || response.data?.message
    }
  }
)

export const addEndpoint = createAsyncThunk(
  `${PUBLIC_API_SLICE_NAME}/add_endpoint`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.addEndpoint(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

// Same `POST /restful_api/webhooks/endpoints` call as `addEndpoint`, kept separate because
// the legacy module had two actions for it (adding an endpoint vs adding event types to an
// existing one).
export const addTypes = createAsyncThunk(
  `${PUBLIC_API_SLICE_NAME}/add_types`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.addTypes(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

/* ------------- Webhook events ------------- */

export const getEvents = createAsyncThunk(
  `${PUBLIC_API_SLICE_NAME}/get_events`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getEvents(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

export const getEvent = createAsyncThunk(
  `${PUBLIC_API_SLICE_NAME}/get_event`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getEvent(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

export const resendEvent = createAsyncThunk(
  `${PUBLIC_API_SLICE_NAME}/resend_event`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.resendEvent(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)
